import { ErrorInfo } from 'react';

import { User } from '@packlink/packlink-sdk';
import * as Sentry from '@sentry/browser';

declare let SENTRY_REF: string;
declare let SENTRY_ENV: string;

export const hasSentry = SENTRY_REF && SENTRY_ENV;

export const initSentry = (): void => {
    if (hasSentry) {
        Sentry.init({
            dsn: config.sentry.dsn,
            environment: SENTRY_ENV,
            integrations: [
                Sentry.extraErrorDataIntegration(),
                Sentry.dedupeIntegration(),
                Sentry.httpClientIntegration(),
            ],
            release: SENTRY_REF,
            /**
             * ResizeObserver:
             *  - https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded/58701523#58701523
             *  - https://forum.sentry.io/t/resizeobserver-loop-limit-exceeded/8402
             */
            ignoreErrors: [
                'ResizeObserver loop limit exceeded',
                'ResizeObserver loop completed with undelivered notifications.',
            ],
        });
    }
};

export const setSentryUser = (user: User): void => {
    if (hasSentry) {
        Sentry.withScope((scope) => scope.setUser({ id: user.id, email: user.email }));
    }
};

export const setTag = (tag: string, value: string): void => {
    if (hasSentry) {
        Sentry.withScope((scope) => scope.setTag(tag, value));
    }
};

export const setSentryErrorInfo = (errorInfo: ErrorInfo): void => {
    if (hasSentry) {
        Sentry.withScope((scope: Sentry.Scope) => {
            Object.keys(errorInfo).forEach((key: string) => {
                scope.setExtra(key, errorInfo[key as keyof ErrorInfo]);
            });
        });
    }
};

export const captureSentryException = (error: Error): void => {
    if (hasSentry) {
        Sentry.captureException(error);
    }
};

export const captureEvent = (message: string, extra: Sentry.Event['extra']): void => {
    if (hasSentry) {
        Sentry.captureEvent({
            extra,
            level: 'info' as Sentry.SeverityLevel,
            message,
        });
    }
};

export const captureMessage = (message: string): void => {
    if (hasSentry) {
        Sentry.captureMessage(message);
    }
};

export const addBreadcrumb = (category: string, message: string): void => {
    if (hasSentry) {
        Sentry.addBreadcrumb({
            category,
            message,
        });
    }
};
